import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  InputLabel,
  Button,
  Snackbar,
  Divider,
  LinearProgress,
  Grid,
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MuiAlert from '@mui/material/Alert';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';

const TOTAL_QUESTIONS = 10;
const RATED_QUESTIONS = 8;

const ANSWER_OPTIONS = [
  ['Excellent', 'Good', 'Fair', 'Poor', 'Very Poor'],
  ['Yes', 'No'],
  ['Yes', 'No'],
  ['Yes', 'No'],
  ['Yes', 'No'],
  ['Yes', 'No'],
  ['Very helpful', 'Helpful', 'Somewhat helpful', 'Not helpful', 'Not very helpful'],
  ['Very likely', 'Likely', 'Somewhat likely', 'Not very likely', 'Not at all likely'],
];

const SECTIONS = [
  {
    title: 'Session Experience',
    description: 'Tell us about the quality of the session and how useful it felt.',
    questionIndices: [0, 1, 2, 3],
  },
  {
    title: 'Logistics & Support',
    description: 'Share how scheduling, setup, and support worked for you.',
    questionIndices: [4, 5, 6, 7],
  },
  {
    title: 'Final Thoughts',
    description: 'Leave closing comments so we can keep improving.',
    questionIndices: [8, 9],
  },
];

const useStyles = makeStyles(() => ({
  buttons: {
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '12px 16px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(4px)',
    borderTop: '1px solid rgba(0,0,0,0.08)',
    zIndex: 1,
  },
  cardContent: {
    padding: '16px',
  },
  questionText: {
    marginBottom: '12px',
  },
  errorText: {
    color: 'red',
    marginTop: '8px',
  },
}));

const QuestionCard = ({
  questionNumber,
  questionText,
  answer,
  onAnswerChange,
  answerOptions,
  comment,
  onCommentChange,
  isAnswerRequired,
  isCommentRequired,
  showAnswerError,
  showCommentError,
}) => {
  const classes = useStyles();
  const hasError = showAnswerError || showCommentError;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: hasError ? 'error.main' : 'divider',
        transition: 'border-color 0.2s ease',
        height: '100%',
        borderRadius: 2,
      }}
    >
      <CardContent className={classes.cardContent} sx={{ height: '100%' }}>
        <FormControl component="fieldset" fullWidth>
          <Typography variant="h6" className={classes.questionText}>
            {questionNumber}. {questionText || 'Question unavailable'}{' '}
            {isAnswerRequired || isCommentRequired ? (
              <Typography component="span" variant="subtitle2" color="error">
                *
              </Typography>
            ) : null}
          </Typography>

          {answerOptions && (
            <ToggleButtonGroup
              exclusive
              value={answer || ''}
              onChange={(_, value) => onAnswerChange(value)}
              aria-label={`answer options for question ${questionNumber}`}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              {answerOptions.map((option) => (
                <ToggleButton key={option} value={option} sx={{ textTransform: 'none' }}>
                  {option}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          {showAnswerError && (
            <Typography className={classes.errorText}>Please choose an option.</Typography>
          )}

          <Box mt={2}>
            <TextField
              fullWidth
              name={`comment-${questionNumber}`}
              multiline
              minRows={2}
              maxRows={6}
              variant="outlined"
              value={comment || ''}
              onChange={(event) => onCommentChange(event.target.value)}
              autoComplete="off"
              placeholder="Add your comments here"
            />
            <Typography variant="caption" color="text.secondary">
              Add your comments here {isCommentRequired ? '*' : '(optional)'}
            </Typography>
            {showCommentError && (
              <Typography className={classes.errorText}>Please share your comments.</Typography>
            )}
          </Box>
        </FormControl>
      </CardContent>
    </Card>
  );
};

const Feedback = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState(() => Array(RATED_QUESTIONS).fill(''));
  const [comments, setComments] = useState(() => Array(TOTAL_QUESTIONS).fill(''));
  const [answerErrors, setAnswerErrors] = useState(() => Array(RATED_QUESTIONS).fill(false));
  const [commentErrors, setCommentErrors] = useState(() => Array(TOTAL_QUESTIONS).fill(false));
  const [touchedAnswers, setTouchedAnswers] = useState(() => Array(RATED_QUESTIONS).fill(false));
  const [touchedComments, setTouchedComments] = useState(() => Array(TOTAL_QUESTIONS).fill(false));
  const [questions, setQuestions] = useState(() => Array(TOTAL_QUESTIONS).fill(''));
  const [templateId, setTemplateId] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    let isMounted = true;

    fetch(`${process.env.REACT_APP_API_URL}/api/feedbackquestiondata/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!isMounted || data.detail || !Array.isArray(data) || !data.length) return;
        const template = data[0];
        setTemplateId(template.template_id ?? '');
        setQuestions(
          Array.from({ length: TOTAL_QUESTIONS }, (_, index) => template[`question${index + 1}`] || '')
        );
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAnswerChange = useCallback((index, value) => {
    setAnswers((prev) => {
      const updated = [...prev];
      updated[index] = value || '';
      return updated;
    });
    setTouchedAnswers((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
    setAnswerErrors((prev) => {
      const updated = [...prev];
      updated[index] = false;
      return updated;
    });
  }, []);

  const handleCommentChange = useCallback((index, value) => {
    setComments((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    setTouchedComments((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
    if (index >= RATED_QUESTIONS) {
      setCommentErrors((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    }
  }, []);

  const validateForm = useCallback(() => {
    const updatedAnswerErrors = Array(RATED_QUESTIONS).fill(false);
    const updatedCommentErrors = Array(TOTAL_QUESTIONS).fill(false);
    let isValid = true;

    for (let i = 0; i < RATED_QUESTIONS; i += 1) {
      if (!answers[i]) {
        updatedAnswerErrors[i] = true;
        isValid = false;
      }
    }

    for (let i = RATED_QUESTIONS; i < TOTAL_QUESTIONS; i += 1) {
      if (!comments[i]) {
        updatedCommentErrors[i] = true;
        isValid = false;
      }
    }

    setAnswerErrors(updatedAnswerErrors);
    setCommentErrors(updatedCommentErrors);
    return isValid;
  }, [answers, comments]);

  const buildSubmissionPayload = useCallback(() => {
    const payload = {
      lab_id: localStorage.getItem('InstanceID'),
      lab_type: localStorage.getItem('labtype'),
      feedback_templateid: templateId,
      feedbackdate: localStorage.getItem('feedbackDate'),
      feedback_no: localStorage.getItem('feedbackno'),
      trainee_name: localStorage.getItem('user'),
      trainee_id: localStorage.getItem('traineeid'),
      feedbackid: localStorage.getItem('feedbackid'),
    };

    for (let i = 0; i < RATED_QUESTIONS; i += 1) {
      payload[`q${i + 1}`] = `Q${i + 1}`;
      payload[`qa${i + 1}`] = answers[i];
      payload[`qc${i + 1}`] = comments[i] || '';
    }

    for (let i = RATED_QUESTIONS; i < TOTAL_QUESTIONS; i += 1) {
      payload[`q${i + 1}`] = `Q${i + 1}`;
      payload[`qc${i + 1}`] = comments[i] || '';
    }

    return payload;
  }, [answers, comments, templateId]);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      setSubmitAttempted(true);

      if (!validateForm()) {
        return;
      }

      const payload = buildSubmissionPayload();

      fetch(`${process.env.REACT_APP_API_URL}/api/feedbackdetail/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
        body: JSON.stringify(payload),
      })
        .then((response) => {
          if (response.ok) {
            setOpenSnackbar(true);
          }
          return response.json();
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [buildSubmissionPayload, validateForm]
  );

  const requiredAnsweredCount = useMemo(() => {
    const answered = answers.filter(Boolean).length;
    const commented = comments
      .slice(RATED_QUESTIONS)
      .filter((value) => Boolean(value && value.trim())).length;
    return answered + commented;
  }, [answers, comments]);

  const totalRequired = TOTAL_QUESTIONS - RATED_QUESTIONS + RATED_QUESTIONS;
  const progressPercentage = Math.round((requiredAnsweredCount / totalRequired) * 100);

  const formHasErrors = useMemo(
    () => answerErrors.some(Boolean) || commentErrors.some(Boolean),
    [answerErrors, commentErrors]
  );

  const renderQuestionCard = useCallback(
    (index) => {
      const questionText = questions[index];
      if (!questionText) {
        return null;
      }

      const isAnswerRequired = index < RATED_QUESTIONS;
      const isCommentRequired = index >= RATED_QUESTIONS;
      const answerOptions = index < ANSWER_OPTIONS.length ? ANSWER_OPTIONS[index] : undefined;

      const showAnswerError =
        isAnswerRequired &&
        answerErrors[index] &&
        (submitAttempted || touchedAnswers[index]);
      const showCommentError =
        isCommentRequired &&
        commentErrors[index] &&
        (submitAttempted || touchedComments[index]);

      return (
        <QuestionCard
          key={index}
          questionNumber={index + 1}
          questionText={questionText}
          answer={answers[index]}
          onAnswerChange={(value) => handleAnswerChange(index, value)}
          answerOptions={answerOptions}
          comment={comments[index]}
          onCommentChange={(value) => handleCommentChange(index, value)}
          isAnswerRequired={isAnswerRequired}
          isCommentRequired={isCommentRequired}
          showAnswerError={showAnswerError}
          showCommentError={showCommentError}
        />
      );
    },
    [answers, comments, questions, answerErrors, commentErrors, submitAttempted, touchedAnswers, touchedComments, handleAnswerChange, handleCommentChange]
  );

  const questionCards = useMemo(
    () =>
      Array.from({ length: RATED_QUESTIONS }, (_, offset) => {
        const index = offset;
        return (
          <QuestionCard
            key={index}
            questionNumber={index + 1}
            questionText={questions[index]}
            answer={answers[index]}
            onAnswerChange={(value) => handleAnswerChange(index, value)}
            answerOptions={ANSWER_OPTIONS[index]}
            comment={comments[index]}
            onCommentChange={(value) => handleCommentChange(index, value)}
            isAnswerRequired={true}
            isCommentRequired={false}
            showAnswerError={answerErrors[index] && (submitAttempted || touchedAnswers[index])}
            showCommentError={commentErrors[index] && (submitAttempted || touchedComments[index])}
          />
        );
      }),
    [answers, comments, questions, answerErrors, commentErrors, submitAttempted, touchedAnswers, touchedComments, handleAnswerChange, handleCommentChange]
  );

  const commentOnlyCards = useMemo(
    () =>
      Array.from({ length: TOTAL_QUESTIONS - RATED_QUESTIONS }, (_, offset) => {
        const index = offset + RATED_QUESTIONS;
        return (
          <QuestionCard
            key={index}
            questionNumber={index + 1}
            questionText={questions[index]}
            comment={comments[index]}
            onCommentChange={(value) => handleCommentChange(index, value)}
            isRequired
            commentError={commentErrors[index]}
          />
        );
      }),
    [comments, questions, handleCommentChange, commentErrors]
  );

  const handleSnackbarClose = useCallback(
    (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }
      setOpenSnackbar(false);
      navigate('/app/feedback');
    },
    [navigate]
  );

  const snackbarAction = useMemo(
    () => (
      <Button color="inherit" size="small" onClick={() => navigate('/app/feedback')}>
        View Feedback List
      </Button>
    ),
    [navigate]
  );

  return (
    <Container maxWidth="xl" sx={{ pb: 6 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Typography component="h1" variant="h4" align="center" gutterBottom>
          Feedback Form
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Thanks for taking a moment to share your thoughts. Your feedback helps us fine-tune future sessions.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Completion progress
          </Typography>
          <LinearProgress variant="determinate" value={progressPercentage} sx={{ borderRadius: 99 }} />
          <Typography variant="caption" color="text.secondary">
            {progressPercentage}% complete
          </Typography>
        </Box>

        {submitAttempted && formHasErrors && (
          <MuiAlert severity="error" sx={{ mb: 3 }}>
            Please review the highlighted questions before submitting.
          </MuiAlert>
        )}

        <Grid container spacing={3} sx={{ mt: 0 }}>
          {questionCards.map((card, idx) => (
            <Grid item xs={12} md={6} key={`qc-${idx}`}>
              {card}
            </Grid>
          ))}
          {commentOnlyCards.map((card, idx) => (
            <Grid item xs={12} md={6} key={`cc-${idx}`}>
              {card}
            </Grid>
          ))}
        </Grid>

        <div className={classes.buttons}>
          <Button type="submit" variant="contained" color="primary" size="medium">
            Submit feedback
          </Button>
        </div>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        action={snackbarAction}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ backgroundColor: '#38761d', fontSize: '20px' }}
        >
          Thanks for your feedback! We're reviewing it to make the next session even better.
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default Feedback;
